import { fromFetch } from 'rxjs/fetch';
import { z } from 'zod/v4';

import { requiredHeaders } from '../../../models/required-headers';
import { unitsOfMeasurementOptions } from '../../../schemas/units-of-measurement.schema';
import { createRequestEnumPreprocessor } from '../../../utils/create-request-enum-preprocessor';
import { parseRequestSchema } from '../../../utils/parse-request-schema';
import { selectData } from '../../../utils/select-data';
import { withJwt } from '../../../utils/with-jwt';
import { routingProfileServiceEnvironment } from '../routing-profile-service-environment';
import { coPilotOptionsSchema } from '../schemas/copilot-options.schema';
import { costProfileSchema } from '../schemas/cost-profile.schema';
import { displayRestrictionTypeOptions } from '../schemas/display-restriction-type.schema';
import { ferriesOptions } from '../schemas/ferries.schema';
import { hazmatTypeOptions } from '../schemas/hazmat-type.schema';
import { routingOptionsSchema } from '../schemas/routing-options.schema';
import { routingProfileSchema } from '../schemas/routing-profile.schema';
import { routingTypeOptions } from '../schemas/routing-type.schema';
import { sideOfStreetAdherenceLevelOptions } from '../schemas/side-of-street-adherence-level.schema';
import { tollRoadTypeOptions } from '../schemas/toll-road-type.schema';
import { tunnelCategoryOptions } from '../schemas/tunnel-category.schema';
import { vehicleOptionsSchema } from '../schemas/vehicle-options.schema';

const requestSchema = routingProfileSchema.partial().required({
  name: true,
  vehicleType: true,
  isAccountDefault: true,
});

const schemaParser = routingProfileSchema.extend({
  routingProfileId: z.number().optional(),
  units: createRequestEnumPreprocessor(unitsOfMeasurementOptions),
  copilotOptions: coPilotOptionsSchema
    .extend({
      displayRestrictions: createRequestEnumPreprocessor(
        displayRestrictionTypeOptions
      ),
    })
    .optional(),
  routingOptions: routingOptionsSchema
    .extend({
      ferries: createRequestEnumPreprocessor(ferriesOptions),
      tollRoads: createRequestEnumPreprocessor(tollRoadTypeOptions),
      routeType: createRequestEnumPreprocessor(routingTypeOptions),
      sideOfStreetAdherenceLevel: createRequestEnumPreprocessor(
        sideOfStreetAdherenceLevelOptions
      ),
    })
    .optional(),
  vehicleOptions: vehicleOptionsSchema
    .extend({
      hazmatType: createRequestEnumPreprocessor(hazmatTypeOptions).optional(),
      tunnelCategory: createRequestEnumPreprocessor(
        tunnelCategoryOptions
      ).optional(),
    })
    .optional(),
  costProfiles: z
    .array(
      z.object({
        profile: costProfileSchema,
      })
    )
    .optional(),
});

/**
 * Update an existing Routing Profile by its ID
 * @param request Updated Routing Profile details
 * @returns A Data object indicating success
 */
export function putRoutingProfilesId(
  this: unknown,
  routingProfileId: number,
  request: z.infer<typeof requestSchema>
) {
  return withJwt((jwtAuthHeaders) =>
    fromFetch(
      new Request(
        `${
          routingProfileServiceEnvironment.resolve(this).routingProfilesUrl
        }/${encodeURIComponent(routingProfileId)}`,
        {
          method: 'PUT',
          headers: {
            ...requiredHeaders,
            ...jwtAuthHeaders,
          },
          body: parseRequestSchema(schemaParser, request),
        }
      )
    )
  ).pipe(
    selectData(
      z.object({
        message: z.string(),
        data: z.boolean(),
        warnings: z.array(z.string()),
      })
    )
  );
}
